import { getHeatmapWeekStartSunday, type HeatmapColumn } from "@/components/charts/heatmap";
import type { ModuleChartHeatmapPoint } from "@/types/modules";

const DAY_MS = 86_400_000;
const WEEK_MS = DAY_MS * 7;
const DAYS_PER_WEEK = 7;

export type HeatmapGrid = {
  columns: HeatmapColumn[];
  /** Null for calendar grids, where the axes derive their own labels. */
  columnLabels: string[] | null;
  rowLabels: string[] | null;
};

function isNumericAxis(values: readonly (string | number)[]): boolean {
  return values.every((value) =>
    typeof value === "number"
      ? Number.isFinite(value)
      : value.trim() !== "" && Number.isFinite(Number(value))
  );
}

function axisKey(value: string | number, numeric: boolean): string {
  return numeric ? String(Number(value)) : String(value);
}

/** Distinct axis values in first-seen order; numeric axes sort ascending. */
function axisOrder(
  values: readonly (string | number)[],
  numeric: boolean
): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const key = axisKey(value, numeric);
    if (!seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  if (numeric) {
    keys.sort((a, b) => Number(a) - Number(b));
  }
  return keys;
}

export function toHeatmapGrid(source: ModuleChartHeatmapPoint[] | undefined): HeatmapGrid {
  const points = source ?? [];
  const xValues = points.map((point) => point.x);
  const yValues = points.map((point) => point.y);
  const xNumeric = points.length > 0 && isNumericAxis(xValues);
  const yNumeric = points.length > 0 && isNumericAxis(yValues);
  const columnKeys = axisOrder(xValues, xNumeric);
  const rowKeys = axisOrder(yValues, yNumeric);

  const counts = new Map<string, number>();
  for (const point of points) {
    counts.set(
      `${axisKey(point.x, xNumeric)}\u0000${axisKey(point.y, yNumeric)}`,
      point.value
    );
  }

  // Columns are laid out as consecutive Sun–Sat weeks ending with the last
  // whole week, so the calendar axes have real dates to read.
  const lastWeekStart = getHeatmapWeekStartSunday(new Date()).getTime() - WEEK_MS;
  const columns = columnKeys.map((columnKey, columnIndex) => ({
    bin: columnIndex,
    bins: rowKeys.map((rowKey, rowIndex) => ({
      bin: rowIndex,
      count: counts.get(`${columnKey}\u0000${rowKey}`) ?? 0,
      date: new Date(
        lastWeekStart -
          (columnKeys.length - 1 - columnIndex) * WEEK_MS +
          rowIndex * DAY_MS
      ),
    })),
  }));

  const isCalendar =
    xNumeric && yNumeric && rowKeys.length === DAYS_PER_WEEK;

  return {
    columns,
    columnLabels: isCalendar ? null : columnKeys,
    rowLabels: isCalendar ? null : rowKeys,
  };
}
